// HUD DOM glue: DNA counter, health bar, damage/orb flashes, hub indicator,
// and the dev depth readout. Owns its element lookups and change-caching so
// main.ts doesn't carry per-frame DOM state.

import type { Entity } from '../game/components'
import { currentBiome, biomeFactor, BIOMES } from '../game/biomes'
import { spurtProgress, spurtRemaining } from '../game/growth-spurt'
import { currentTier, MAX_TIER } from '../game/evolution'
import { T } from '../game/tunables'

export interface Hud {
  update(player: Entity, showDepth: boolean): void
  flashVignette(): void
  flashOrbPickup(): void
  /** Pop a "+N" next to the DNA counter; rapid gains accumulate into one pop. */
  popDna(amount: number): void
}

// Restart a CSS opacity transition from 1 → 0. The forced reflow between the
// two writes is what re-arms the transition.
function flash(el: HTMLElement | null, durationSec: number): void {
  if (!el) return
  el.style.transition = 'none'
  el.style.opacity = '1'
  void el.offsetHeight
  el.style.transition = `opacity ${durationSec}s ease-out`
  el.style.opacity = '0'
}

export function createHud(): Hud {
  const dnaCount = document.getElementById('hud-dna-count')
  const tierEl = document.getElementById('hud-tier')
  const healthFill = document.getElementById('hud-health-fill')
  const spurtFill = document.getElementById('hud-spurt-fill')
  const spurtCount = document.getElementById('hud-spurt-count')
  const vignette = document.getElementById('damage-vignette')
  const orbFlash = document.getElementById('orb-flash')
  const hubIndicator = document.getElementById('hub-indicator')
  const devDepthEl = document.getElementById('dev-depth')
  const dnaPop = document.getElementById('hud-dna-pop')
  const depthMarker = document.getElementById('depth-rail-marker')
  const depthZone = document.getElementById('depth-rail-zone')

  // Depth rail: vertical, shallow at top → deep → mushroom floor at bottom.
  // Spans 0 → the outermost band; ticks mark each biome boundary.
  const RAIL_MAX = T.biomes.mushroomOuter
  {
    const ticks = document.querySelectorAll<HTMLElement>('.depth-rail-tick')
    for (const tick of ticks) {
      const d = tick.dataset.tick === 'hub'
        ? T.biomes.hubRadius
        : tick.dataset.tick === 'deep'
          ? (T.biomes.inner + T.biomes.outer) / 2
          : (T.biomes.mushroomInner + T.biomes.mushroomOuter) / 2 // 'mushroom'
      tick.style.top = `${(d / RAIL_MAX) * 100}%`
    }
  }

  // Accumulator for the "+N" pop: rapid pickups merge instead of stacking.
  let popTotal = 0
  let popTimer: ReturnType<typeof setTimeout> | null = null

  let lastDna = -1
  let lastTier = -1
  let lastHp = -1
  let lastSpurt = -1
  let lastSpurtRemaining = -1
  let lastDepthPct = -1
  let lastZone = ''

  return {
    update(player, showDepth) {
      const hp = player.health
      if (hp && healthFill) {
        const pct = Math.max(0, hp.current / hp.max) * 100
        if (pct !== lastHp) {
          healthFill.style.width = `${pct}%`
          lastHp = pct
        }
      }
      const dna = player.dna ?? 0
      if (dna !== lastDna && dnaCount) {
        dnaCount.textContent = String(dna)
        lastDna = dna
      }
      const tier = currentTier(player)
      if (tier !== lastTier && tierEl) {
        tierEl.textContent = `${tier} / ${MAX_TIER}`
        lastTier = tier
      }
      const spurtPct = Math.round(spurtProgress(player) * 100)
      if (spurtPct !== lastSpurt && spurtFill) {
        spurtFill.style.width = `${spurtPct}%`
        lastSpurt = spurtPct
      }
      const remaining = spurtRemaining(player)
      if (remaining !== lastSpurtRemaining && spurtCount) {
        spurtCount.textContent = String(remaining)
        lastSpurtRemaining = remaining
      }
      const dist = Math.hypot(player.position.x, player.position.y)
      const atHub = dist < T.biomes.hubRadius
      hubIndicator?.classList.toggle('visible', atHub)
      // Depth rail: the arrow marker slides down 0 → outermost band. The zone
      // word is fixed beneath the rail (a caption), so only the marker moves.
      const depthPct = Math.round(Math.min(1, dist / RAIL_MAX) * 200) / 2
      if (depthPct !== lastDepthPct) {
        if (depthMarker) depthMarker.style.top = `${depthPct}%`
        lastDepthPct = depthPct
      }
      const zone = atHub ? 'home water' : BIOMES[currentBiome(player)].displayName
      if (zone !== lastZone && depthZone) {
        depthZone.textContent = zone
        lastZone = zone
      }
      if (showDepth && devDepthEl) {
        const d = Math.hypot(player.position.x, player.position.y)
        devDepthEl.textContent =
          `depth ${d.toFixed(1)}  ·  blend ${biomeFactor(player).toFixed(2)}  ·  ${currentBiome(player)}` +
          `  (boundary ${T.biomes.inner}–${T.biomes.outer})`
      }
    },
    flashVignette() {
      flash(vignette, 0.35)
    },
    flashOrbPickup() {
      flash(orbFlash, 0.25)
    },
    popDna(amount) {
      if (!dnaPop) return
      popTotal += amount
      dnaPop.textContent = `+${popTotal}`
      flash(dnaPop, 1.0)
      if (popTimer !== null) clearTimeout(popTimer)
      // Reset the accumulator once the pop has faded and no gain refreshed it.
      popTimer = setTimeout(() => {
        popTotal = 0
        popTimer = null
      }, 1000)
    },
  }
}
